import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ShieldCheck, Gavel, FileCheck } from "lucide-react";
import api from "../lib/api";
import ListingCard from "../components/ListingCard";

const EASE = [0.76, 0, 0.24, 1];
const LOGO = "https://customer-assets-39nsmqrw.emergentagent.net/job_gunbri-broker/artifacts/egembtay_image.png";

function MaskLine({ children, delay = 0 }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, ease: EASE, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function Reveal({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, ease: EASE, delay }}
    >
      {children}
    </motion.div>
  );
}

const CHAPTERS = [
  { n: "01", t: "Source", d: "We curate rare, tactical, and collector-grade firearms from vetted sellers. No noise. Only iron worth owning." },
  { n: "02", t: "Broker", d: "Place an offer, open a private thread, negotiate directly. Every listing runs through our brokerage desk." },
  { n: "03", t: "Transfer", d: "Reserve with a 10% deposit. Final transfer completes through a licensed FFL near you. Compliant, always." },
];

const FEATURES = [
  { icon: ShieldCheck, t: "FFL Compliant", d: "Every transfer routed through licensed dealers." },
  { icon: Gavel, t: "Live Offers", d: "In-app negotiation between buyer and seller." },
  { icon: FileCheck, t: "Verified Iron", d: "Curated, condition-graded, honestly described." },
];

export default function Landing() {
  const [listings, setListings] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  useEffect(() => {
    api.get("/listings").then((r) => setListings(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div className="relative z-10">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen pt-28 md:pt-32 pb-16 overflow-hidden tac-grid">
        {/* HUD status strip */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          className="absolute top-16 left-0 right-0 border-b border-[#5A2A24] bg-[#1E070A]/60 backdrop-blur-sm z-20">
          <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-9 flex items-center justify-between text-[0.65rem] font-mono tracking-widest uppercase text-[#8C6552]">
            <span className="flex items-center gap-2"><span className="live-dot" /> <span className="text-[#9DB562]">Signal: Live</span></span>
            <span className="hidden sm:inline">LAT 34.0522 · LON -118.2437</span>
            <span className="hidden md:inline text-amber">SEC//CLEARANCE: CIVILIAN</span>
            <span>OPS <span className="text-[#EFE4D2]">24 / 7</span></span>
          </div>
        </motion.div>

        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center pt-10">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
                        className="mono-label mb-6 flex items-center gap-3">
              <span className="warning-stripe w-10 h-3" /> Mission // Shooter Bazaar Firearms Brokerage · EST. 2026
            </motion.div>
            <h1 className="font-display uppercase text-6xl sm:text-7xl lg:text-[7.5rem] leading-[0.85] tracking-tight">
              <MaskLine delay={0.15}>Own the</MaskLine>
              <MaskLine delay={0.28}><span className="stroke-text">Bazaar</span></MaskLine>
              <MaskLine delay={0.41}>Arsenal</MaskLine>
            </h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }}
                      className="mt-8 text-[#C7A98F] text-lg max-w-lg">
              A curated brokerage for tactical hardware and collector iron. Browse, negotiate, and reserve — routed through licensed dealers.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.8 }}
                        className="mt-10 flex flex-wrap gap-4">
              <Link to="/marketplace" data-testid="hero-browse" className="btn-tactical">Enter Marketplace <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/auth?mode=register" data-testid="hero-join" className="btn-ghost">Sell Your Iron</Link>
            </motion.div>
          </div>
          <div className="lg:col-span-5 relative">
            <motion.div style={{ y: imgY }} className="relative hud-frame"
              initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.1, ease: EASE, delay: 0.4 }}>
              <div className="relative border border-[#5A2A24] bg-[#2C0D12] p-5 md:p-7 overflow-hidden">
                <div className="absolute inset-0 tac-grid opacity-40" />
                <motion.img style={{ scale: imgScale }} src={LOGO} alt="Shooter Bazaar emblem"
                  className="relative w-full aspect-square object-contain" />
              </div>
              <span className="hud-corner tl" /><span className="hud-corner tr" />
              <span className="hud-corner bl" /><span className="hud-corner br" />
              <div className="absolute -bottom-4 -left-4 bg-[#C0894F] text-white font-mono text-xs px-4 py-3 tracking-widest clip-notch z-[6]">
                LIVE INVENTORY
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="py-8 border-y border-[#5A2A24] overflow-hidden">
        <Marquee speed={40} gradient={false}>
          {["RIFLES", "PISTOLS", "REVOLVERS", "TACTICAL", "COLLECTOR", "COMPLIANT", "BROKERED"].map((w, i) => (
            <span key={i} className="font-display uppercase text-6xl md:text-8xl mx-8 stroke-text-dim">{w} —</span>
          ))}
        </Marquee>
      </section>

      {/* FEATURES */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-24">
        <div className="grid grid-cols-1 md:grid-cols-3 border-t border-l border-[#5A2A24]">
          {FEATURES.map((f, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="border-r border-b border-[#5A2A24] p-8 md:p-10 h-full hover:bg-[#2C0D12] transition-colors">
                <f.icon className="w-8 h-8 text-[#C0894F] mb-6" />
                <h3 className="font-display text-2xl uppercase mb-2">{f.t}</h3>
                <p className="text-[#C7A98F]">{f.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* MANIFESTO */}
      <section id="manifesto" className="max-w-[1400px] mx-auto px-5 md:px-10 py-24">
        <Reveal><div className="mono-label mb-4">/ The Protocol</div></Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-display uppercase text-4xl sm:text-6xl mb-16 max-w-3xl">How the brokerage works</h2>
        </Reveal>
        <div className="space-y-0 border-t border-[#5A2A24]">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.08}>
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 py-10 border-b border-[#5A2A24] group hover:bg-[#2C0D12] transition-colors px-2">
                <div className="md:col-span-2 font-mono text-4xl md:text-6xl text-[#C0894F]">{c.n}</div>
                <div className="md:col-span-3"><h3 className="font-display text-3xl uppercase">{c.t}</h3></div>
                <div className="md:col-span-7 text-[#C7A98F] text-lg max-w-2xl">{c.d}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FEATURED LISTINGS */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-24">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <Reveal><div className="mono-label mb-4">/ Featured Iron</div></Reveal>
            <Reveal delay={0.1}><h2 className="font-display uppercase text-4xl sm:text-6xl">On the desk</h2></Reveal>
          </div>
          <Link to="/marketplace" className="btn-ghost" data-testid="featured-viewall">View All <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {listings.map((l, i) => (
            <Reveal key={l.id} delay={i * 0.1}><ListingCard listing={l} index={i} /></Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-24">
        <Reveal>
          <div className="border border-[#5A2A24] bg-[#2C0D12] p-10 md:p-20 text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_50%_0%,#C0894F,transparent_60%)]" />
            <div className="relative">
              <h2 className="font-display uppercase text-4xl sm:text-6xl mb-4">Ready to trade iron?</h2>
              <p className="text-[#C7A98F] mb-8 max-w-xl mx-auto">List your firearm or reserve your next piece. The Bazaar desk is open.</p>
              <Link to="/auth?mode=register" className="btn-tactical" data-testid="cta-join">Create Account <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
